import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import { useInView } from 'react-intersection-observer';

import { Button } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';
import { useScrollToSection } from '#src/app/pages/petitionDetails/shared/hooks';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import type { MediaHitEmbed } from '../../shared/context';
import { useMediaHitsContext } from '../../shared/context';
import { MediaHitList } from '../MediaHitList';
import { useMediaHitScrollControl } from '../MediaHitList/hooks/useMediaHitScrollControl';
import { MediaHitsModal } from '../MediaHitsModal';

import { MediaHitsHeader } from './components/MediaHitsHeader';
import { useMediaHits } from './hooks/useMediaHits';

type Props = Readonly<{
	/** Page-slot spacing. Applied here, not on the slot, so an unresolved section leaves no blank band. */
	className?: string;
}>;

export const MediaHitsWrapper: React.FC<Props> = ({ className }) => {
	// Rows the modal loads beyond the preview's initial embeds. `useMediaHits`'s click listener checks this ref
	// too, so clicks on modal-only rows resolve `media_source` instead of reporting it as undefined.
	const modalLoadedEmbedsRef = useRef<readonly MediaHitEmbed[]>([]);
	const handleModalEmbedsLoaded = useCallback((newEmbeds: readonly MediaHitEmbed[]) => {
		modalLoadedEmbedsRef.current = [...modalLoadedEmbedsRef.current, ...newEmbeds];
	}, []);

	const {
		data: { showAdd, showSection, showList, hasMediaHits, showSeeAll, trackingData },
	} = useMediaHits(modalLoadedEmbedsRef);
	const { track } = useCampaignTracking();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleOpenModal = () => {
		track('media_hit_see_all_click', trackingData);
		setIsModalOpen(true);
	};
	const {
		actions: { loadEmbeds },
	} = useMediaHitsContext();

	const {
		data: { refs },
	} = useScrollToSection();

	const mediaHitListRef = useRef<HTMLDivElement>(null);
	const {
		data: { canScrollLeft, canScrollRight, hasOverflow },
		actions: { updateScrollState, scrollByOneMediaHit },
	} = useMediaHitScrollControl(mediaHitListRef);

	// Defer embed loading until the section nears the viewport. The trigger lives on an always-rendered sentinel
	// (rather than the section itself) because the section stays hidden until at least one embed resolves valid —
	// gating the trigger on the section would deadlock that case.
	const { ref: loadTriggerRef, inView } = useInView({ rootMargin: '600px', triggerOnce: true });
	useEffect(() => {
		if (inView) loadEmbeds();
	}, [inView, loadEmbeds]);

	// Update scroll state when content visibility changes (with delay for rendering)
	useEffect(() => {
		if (showList) {
			// Delay to allow MediaHitList to render its content
			const timer = setTimeout(updateScrollState, 100);
			return () => clearTimeout(timer);
		}

		return undefined;
	}, [showList, updateScrollState]);

	if (!showSection && !hasMediaHits) {
		return null;
	}

	// The scroll anchor (id + ref) stays mounted whenever there are media hits, even while the visible section is
	// still hidden pending embed resolution. Otherwise `scrollToSection(MEDIA_MENTIONS)` (fired by the content
	// summary / momentum bar links) would no-op, and the lazy load — which depends on this region scrolling into
	// view — would never trigger.
	return (
		<div id={SECTION_ID_MAPPING.MEDIA_MENTIONS} ref={refs[SECTION_ID_MAPPING.MEDIA_MENTIONS]}>
			{hasMediaHits && <span ref={loadTriggerRef} aria-hidden="true" className="block h-0 w-0" />}
			{showSection && (
				<div data-testid="media-hits-wrapper" className={cn('overflow-x-hidden', className)}>
					<MediaHitsHeader
						showAdd={showAdd}
						trackingData={trackingData}
						onScrollLeft={() => scrollByOneMediaHit('left')}
						onScrollRight={() => scrollByOneMediaHit('right')}
						canScrollLeft={canScrollLeft}
						canScrollRight={canScrollRight}
						showScrollButtons={hasOverflow}
					/>

					{showList && (
						<>
							<MediaHitList ref={mediaHitListRef} />
							{showSeeAll && (
								<div>
									<Button
										variant="tertiary"
										size="small"
										className="mt-3 p-0"
										onClick={handleOpenModal}
										data-testid="media-hits-see-all"
										data-qa="media-hits-see-all"
									>
										{/* Borrowing the signed-petition-home "see all" copy until this button gets its own approved translations. */}
										<Translate value="corgi.signed_petition_home.media_mentions_update_card.see_all" />
									</Button>
								</div>
							)}
						</>
					)}
				</div>
			)}
			{showSection && (
				<MediaHitsModal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
					onEmbedsLoaded={handleModalEmbedsLoaded}
				/>
			)}
		</div>
	);
};
